import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Video, MapPin, ExternalLink, Clock, ChevronRight } from 'lucide-react';
import { format, isToday, isTomorrow, formatDistanceToNow } from 'date-fns';

/**
 * Mobile Upcoming Meetings component for the Dashboard.
 * Shows real Calendly meetings synced via OAuth.
 */
const MobileUpcomingMeetings = ({ meetings, loading, calendlyConnected }) => {
    const navigate = useNavigate();

    if (!calendlyConnected) {
        return (
            <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-notion-text flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-stone-400" />
                        Meetings
                    </h2>
                </div>

                <div className="border border-dashed border-stone-300 rounded-lg p-6 text-center bg-white">
                    <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Calendar className="w-5 h-5 text-blue-500" />
                    </div>
                    <p className="text-xs text-stone-500 mb-3">
                        Connect Calendly to see meetings
                    </p>
                    <button
                        onClick={() => navigate('/settings?section=integrations')}
                        className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded text-xs font-medium active:bg-blue-700"
                    >
                        <ExternalLink className="w-3 h-3" />
                        Settings
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-notion-text flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-stone-400" />
                    Meetings
                </h2>
                {meetings.length > 0 && (
                    <span className="text-[10px] text-notion-text-light">
                        {meetings.length} upcoming
                    </span>
                )}
            </div>

            {loading ? (
                <div className="space-y-2">
                    {[1, 2].map((i) => (
                        <div key={i} className="p-3.5 rounded-lg border border-notion-border bg-white shadow-sm animate-pulse">
                            <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
                            <div className="h-4 w-40 bg-gray-200 rounded mb-1" />
                            <div className="h-3 w-28 bg-gray-200 rounded" />
                        </div>
                    ))}
                </div>
            ) : meetings.length === 0 ? (
                <div className="border border-notion-border rounded-lg p-6 text-center bg-white shadow-sm">
                    <p className="text-xs text-notion-text-light">No upcoming meetings</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {meetings.map((meeting) => (
                        <MobileMeetingCard key={meeting.id} meeting={meeting} />
                    ))}
                </div>
            )}
        </div>
    );
};

const MobileMeetingCard = ({ meeting }) => {
    const startDate = meeting.startTime instanceof Date ? meeting.startTime : new Date(meeting.startTime);
    const endDate = meeting.endTime instanceof Date ? meeting.endTime : new Date(meeting.endTime);
    const isVideoCall = meeting.location?.includes('http') || meeting.location?.includes('zoom') || meeting.location?.includes('meet');
    const Icon = isVideoCall ? Video : MapPin;

    const formatDayLabel = (date) => {
        if (isToday(date)) return 'Today';
        if (isTomorrow(date)) return 'Tomorrow';
        return format(date, 'EEE, MMM d');
    };

    const timeRange = `${format(startDate, 'h:mm a')} – ${format(endDate, 'h:mm a')}`;
    const dayLabel = formatDayLabel(startDate);

    return (
        <div className="p-3.5 rounded-lg border border-notion-border bg-white shadow-sm active:bg-notion-sidebar transition-colors">
            <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[11px] font-semibold text-status-good-text">{dayLabel}</span>
                    </div>
                    <div className="text-sm font-medium text-notion-text truncate">{meeting.name}</div>
                    {(meeting.inviteeName || meeting.inviteeEmail) && (
                        <div className="text-xs text-notion-text-light mt-0.5 truncate">
                            with {meeting.inviteeName || meeting.inviteeEmail}
                        </div>
                    )}
                    <div className="flex items-center gap-1.5 mt-1.5">
                        <Clock className="w-3 h-3 text-stone-400" />
                        <span className="text-[11px] text-stone-500">{timeRange}</span>
                    </div>
                </div>
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-stone-100 flex-shrink-0 ml-3">
                    <Icon className="w-4 h-4 text-stone-500" />
                </div>
            </div>
        </div>
    );
};

export default MobileUpcomingMeetings;
